import { useEffect, useRef } from 'react'
import { reverseLocality } from '@/components/geocode'
import { log } from 'utils/logger'
import { decodePolyline } from '@/components/mapPolyline'
import { pinSvg } from '@/components/mapPins'
import { RouteAlternative } from 'features/load-hub/api/tollRoutes.api'
import { ROUTE_PIN_DESTINATION_COLOR, ROUTE_PIN_ORIGIN_COLOR } from '@/constants/mapPins'

export type LatLng = { lat: number; lng: number }

/** One route leg's human-readable distance + drive time (Google Directions `leg.distance/duration.text`). */
export interface Leg {
    distance: string
    duration: string
}

/** The map's route result: the two legs (driver→pickup, pickup→delivery) plus whether Directions FAILED, so
 *  the caller can distinguish "still calculating" from "route unavailable". */
export interface RouteLegs {
    toOrigin: Leg | null
    toDest: Leg | null
    failed: boolean
}

/** Coerce a coordinate to a FINITE number, or null. Coords can arrive as strings ("37.5") or NaN depending on
 *  the load source; Google Maps rejects anything that isn't a real number ("not a LatLng ... not a number"),
 *  and an unguarded bad value crashes the map to the app ErrorBoundary. */
const finiteCoord = (v: number | string | null | undefined): number | null => {
    const n = typeof v === 'string' ? parseFloat(v) : v
    return typeof n === 'number' && Number.isFinite(n) ? n : null
}
export const toLatLng = (lat: number | string | null | undefined, lng: number | string | null | undefined): LatLng | null => {
    const la = finiteCoord(lat)
    const ln = finiteCoord(lng)
    return la != null && ln != null ? { lat: la, lng: ln } : null
}

/** A Numeo teardrop pin (shared with the Leaflet map) as a Google marker icon. */
const pinIcon = (g: typeof google.maps, color: string, letter: string): google.maps.Icon => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(pinSvg(color, letter))}`,
    scaledSize: new g.Size(28, 40),
    anchor: new g.Point(14, 40),
})

interface NearbyDriverRouteMapProps {
    driver: LatLng
    origin: LatLng
    destination: LatLng
    tollRoutes: RouteAlternative[]
    selectedRouteIndex: number
    showTolls: boolean
    onResult: (legs: RouteLegs) => void
    onDriverPlace: (place: string) => void
}

/**
 * The Google map + the driving route driver → load PICKUP → load DELIVERY, drawn with a single Directions call
 * (waypoints=[pickup]). Rendered ONLY inside <GoogleMapsLoader> (window.google.maps is ready). Three shared
 * Numeo pins (A driver / B origin / C destination) are dropped and the map is fit to them up front, so the
 * points show even if the Directions request is slow or denied. Reports the two legs up (leg[0] = driver→pickup
 * ETA, leg[1] = the load) plus a `failed` flag, and reverse-geocodes the driver point so the header can name the
 * driver's location when the profile has no city/state.
 *
 * When toll routes are fetched on demand (the reused load-hub "Get Tolls" flow), the selected toll route's
 * polyline is overlaid in emerald with a dot at every toll plaza — the same information the load-hub Leaflet
 * map shows, drawn here with Google primitives.
 */
export const NearbyDriverRouteMap = ({
    driver,
    origin,
    destination,
    tollRoutes,
    selectedRouteIndex,
    showTolls,
    onResult,
    onDriverPlace,
}: NearbyDriverRouteMapProps) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const mapRef = useRef<google.maps.Map | null>(null)
    // Toll overlay layers (polylines + plaza markers) — cleared and redrawn when the selection changes.
    const tollLayersRef = useRef<Array<{ setMap: (m: google.maps.Map | null) => void }>>([])

    useEffect(() => {
        const g = window.google?.maps
        if (!g || !containerRef.current) return
        let cancelled = false
        const map = new g.Map(containerRef.current, { center: origin, zoom: 6, mapTypeControl: false, streetViewControl: false, fullscreenControl: false })
        mapRef.current = map

        // Drop the three shared Numeo pins and frame them immediately — the map is useful even before
        // Directions returns.
        const markers = [
            new g.Marker({ position: driver, map, title: 'Driver', icon: pinIcon(g, '#3B82F6', 'A') }),
            new g.Marker({ position: origin, map, title: 'Origin (pickup)', icon: pinIcon(g, ROUTE_PIN_ORIGIN_COLOR, 'B') }),
            new g.Marker({ position: destination, map, title: 'Destination (delivery)', icon: pinIcon(g, ROUTE_PIN_DESTINATION_COLOR, 'C') }),
        ]
        const bounds = new g.LatLngBounds()
        ;[driver, origin, destination].forEach((p) => bounds.extend(p))
        map.fitBounds(bounds, 64)

        // Name the driver's location for the header (their profile often has no city/state).
        reverseLocality(driver)
            .then((locality) => {
                if (cancelled) return
                const label = [locality?.city, locality?.state].filter(Boolean).join(', ')
                if (label) onDriverPlace(label)
            })
            .catch((error: unknown) => log.w('[box-truck] driver reverse-geocode failed', error))

        const renderer = new g.DirectionsRenderer({ map, suppressMarkers: true, polylineOptions: { strokeColor: '#2563EB', strokeWeight: 5 } })
        new g.DirectionsService().route(
            { origin: driver, destination, waypoints: [{ location: origin, stopover: true }], travelMode: g.TravelMode.DRIVING },
            (result, status) => {
                if (cancelled) return
                const toLeg = (l?: google.maps.DirectionsLeg): Leg | null =>
                    l ? { distance: l.distance?.text ?? '—', duration: l.duration?.text ?? '—' } : null
                if (status === g.DirectionsStatus.OK && result) {
                    renderer.setDirections(result)
                    const legs = result.routes[0]?.legs ?? []
                    onResult({ toOrigin: toLeg(legs[0]), toDest: toLeg(legs[1]), failed: false })
                } else {
                    console.warn('[box-truck] driver→pickup→delivery directions failed', status)
                    onResult({ toOrigin: null, toDest: null, failed: true })
                }
            }
        )

        // Tear the map down on close: detach markers + route so a repeatedly opened modal doesn't leak
        // detached Google Map instances, and late geocode/Directions callbacks don't setState after unmount.
        return () => {
            cancelled = true
            markers.forEach((m) => m.setMap(null))
            renderer.setMap(null)
            mapRef.current = null
        }
    }, [driver, origin, destination, onResult, onDriverPlace])

    // Toll overlay — redraw the selected route's polyline + plaza dots whenever the toll selection changes.
    useEffect(() => {
        const g = window.google?.maps
        const map = mapRef.current
        if (!g || !map) return
        tollLayersRef.current.forEach((l) => l.setMap(null))
        tollLayersRef.current = []
        if (!showTolls || tollRoutes.length === 0) return

        tollRoutes.forEach((route, index) => {
            const path = decodePolyline(route.polyline).map(([lat, lng]) => ({ lat, lng }))
            if (path.length === 0) return
            const isSelected = index === selectedRouteIndex
            const line = new g.Polyline({
                path,
                map,
                strokeColor: isSelected ? '#10B981' : '#9CA3AF',
                strokeWeight: isSelected ? 5 : 3,
                strokeOpacity: isSelected ? 1 : 0.45,
                zIndex: isSelected ? 20 : 1,
            })
            tollLayersRef.current.push(line)
            if (isSelected && route.tollLocations) {
                route.tollLocations.forEach((t) => {
                    const ll = toLatLng(t.lat, t.lng)
                    if (!ll) return
                    const dot = new g.Marker({
                        position: ll,
                        map,
                        title: t.cost ? `${t.name} · $${t.cost.amount}` : t.name,
                        icon: { path: g.SymbolPath.CIRCLE, scale: 5, fillColor: '#10B981', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 1.5 },
                    })
                    tollLayersRef.current.push(dot)
                })
            }
        })
        return () => {
            tollLayersRef.current.forEach((l) => l.setMap(null))
            tollLayersRef.current = []
        }
    }, [tollRoutes, selectedRouteIndex, showTolls])

    return <div ref={containerRef} className="h-[64vh] min-h-[420px] w-full" />
}
